/**
 * LocalStorage-backed draft for the tenant provisioning wizard.
 *
 * Server-side `TenantDraft` (schema.prisma + D1.1.2 cleanup job) exists but
 * the CREATE/READ endpoints land with the per-step server actions in
 * D1.5.2+. Until then the shell persists client-side so reloads restore
 * step position and partial payload — the acceptance criterion for D1.5.1.
 */

export const TENANT_WIZARD_STEPS = [
  'basic-info',
  'domain',
  'brand',
  'config',
  'modules',
  'admin-user',
  'review',
] as const;

export type TenantWizardStep = (typeof TENANT_WIZARD_STEPS)[number];

export const TENANT_WIZARD_STEP_COUNT = TENANT_WIZARD_STEPS.length;

export const TENANT_WIZARD_STEP_LABELS: Record<TenantWizardStep, string> = {
  'basic-info': 'Basic info',
  domain: 'Domain',
  brand: 'Brand kit',
  config: 'Config',
  modules: 'Modules',
  'admin-user': 'First owner',
  review: 'Review',
};

const DRAFT_SCHEMA_VERSION = 1;
const DRAFT_STORAGE_KEY = 'ferro.admin.tenantDraft.v1';

export type TenantWizardDraft = {
  version: typeof DRAFT_SCHEMA_VERSION;
  lastStep: TenantWizardStep;
  payload: Record<string, unknown>;
  updatedAt: string;
};

function isTenantWizardStep(value: unknown): value is TenantWizardStep {
  return typeof value === 'string'
    && (TENANT_WIZARD_STEPS as readonly string[]).includes(value);
}

function isPayloadRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseStoredDraft(raw: string | null): TenantWizardDraft | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPayloadRecord(parsed)) return null;
  if (parsed.version !== DRAFT_SCHEMA_VERSION) return null;
  if (!isTenantWizardStep(parsed.lastStep)) return null;
  if (!isPayloadRecord(parsed.payload)) return null;
  if (typeof parsed.updatedAt !== 'string') return null;
  return {
    version: DRAFT_SCHEMA_VERSION,
    lastStep: parsed.lastStep,
    payload: parsed.payload,
    updatedAt: parsed.updatedAt,
  };
}

export function readDraft(): TenantWizardDraft | null {
  return parseStoredDraft(window.localStorage.getItem(DRAFT_STORAGE_KEY));
}

export function writeDraft(
  draft: Omit<TenantWizardDraft, 'version' | 'updatedAt'>,
  now: Date = new Date(),
): TenantWizardDraft {
  const full: TenantWizardDraft = {
    version: DRAFT_SCHEMA_VERSION,
    lastStep: draft.lastStep,
    payload: draft.payload,
    updatedAt: now.toISOString(),
  };
  window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(full));
  return full;
}

export function clearDraft(): void {
  window.localStorage.removeItem(DRAFT_STORAGE_KEY);
}

export function stepIndex(step: TenantWizardStep): number {
  return TENANT_WIZARD_STEPS.indexOf(step);
}

export function nextStep(step: TenantWizardStep): TenantWizardStep | null {
  const idx = stepIndex(step);
  return idx === TENANT_WIZARD_STEP_COUNT - 1 ? null : TENANT_WIZARD_STEPS[idx + 1]!;
}

export function prevStep(step: TenantWizardStep): TenantWizardStep | null {
  const idx = stepIndex(step);
  return idx === 0 ? null : TENANT_WIZARD_STEPS[idx - 1]!;
}

export const TENANT_DRAFT_STORAGE_KEY = DRAFT_STORAGE_KEY;
